import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CsAgentRun } from '../../../common/models/content-studio.model';
import { useAskAi } from '../assistant/AskAiContext';
import { ActionTrail } from '../content-studio/planner/AgentTurnCards';
import { relativeTime } from '../insights/insight-presentation';
import { isLive, isPaused, profileLabelKey, runRoute, stepVerb } from './work-presentation';
import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { useAgentWorkSignal } from './agent-work.store';

const STATUS_TONE: Record<CsAgentRun['status'], string> = {
    pending: 'bg-muted text-muted-foreground',
    running: 'bg-muted text-foreground',
    // A decision is outstanding, so it reads like the spend it is waiting on.
    paused: 'bg-warning/15 text-warning',
    succeeded: 'bg-success/15 text-success',
    failed: 'bg-destructive/10 text-destructive',
    cancelled: 'bg-muted text-muted-foreground',
};

/**
 * One piece of work, in full.
 *
 * Opened from a row on the dashboard, which shows only the profile and the
 * step it is on. What that row cannot say is what was asked, everything the
 * agent did to answer it, and what came out — so that is what this is.
 *
 * The same view whether the run is finished or still going: a live one simply
 * has fewer steps so far, and the trail marks the last as in progress. Two
 * layouts for the same thing would only differ while you watched.
 */
export const RunDetail: React.FC<{
    run: CsAgentRun;
    onOpenAll: () => void;
    /** Closes the drawer when a conversation is opened over it. */
    onLeave?: () => void;
}> = ({ run, onOpenAll, onLeave }) => {
    const { t } = useTranslation();
    const askAi = useAskAi();
    const studio = useContentStudioApi();
    const live = isLive(run);
    const paused = isPaused(run);
    // Guards the two calls below against a double click, which would otherwise
    // dispatch the same run twice.
    const [busy, setBusy] = useState(false);
    const route = runRoute(run);
    const summary = (run.result as { summary?: unknown } | undefined)?.summary;
    const when = relativeTime(run.updatedAt || run.createdAt);

    /*
     * The conversation this came out of, when there was one.
     *
     * Ask AI records its thread here. A studio run's conversation is the
     * planner panel inside its project, which is what "Open in studio" below
     * already goes to — so it needs no second link.
     */
    const conversation = typeof run.context?.assistantThreadUuid === 'string'
        ? (run.context.assistantThreadUuid as string)
        : null;

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
                <Badge className={cn('border-transparent', STATUS_TONE[run.status])}>
                    {t(`WORK.STATUS.${run.status.toUpperCase()}`)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                    {`${t(profileLabelKey(run.profile))} · ${t(when.key, { count: when.count })}`}
                </span>
            </div>

            <div>
                <h3 className="mb-1 text-xs font-medium text-muted-foreground">{t('WORK.DETAIL.ASKED')}</h3>
                <p className="text-sm leading-relaxed">{run.message}</p>
            </div>

            {(run.steps?.length ?? 0) > 0 && (
                <div>
                    <h3 className="mb-2 text-xs font-medium text-muted-foreground">{t('WORK.DETAIL.STEPS')}</h3>
                    <ActionTrail actions={(run.steps ?? []).map((s) => ({ verb: stepVerb(s.tool) }))} live={live} />
                </div>
            )}

            {typeof summary === 'string' && summary.trim() && (
                <div>
                    <h3 className="mb-1 text-xs font-medium text-muted-foreground">{t('WORK.DETAIL.RESULT')}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{summary}</p>
                </div>
            )}

            {/*
              * It stopped because it had spent its allowance.
              *
              * Said in credits rather than in tokens or dollars: credits are
              * what the vendor buys and what the sidebar shows them, and this
              * is a question about whether to spend more of them.
              */}
            {paused && (
                <div>
                    <h3 className="mb-1 text-xs font-medium text-muted-foreground">{t('WORK.DETAIL.PAUSED')}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                        {t('WORK.TOAST.PAUSED_HINT', { credits: run.creditsSpent ?? 0 })}
                    </p>
                </div>
            )}

            {run.error && (
                <div>
                    <h3 className="mb-1 text-xs font-medium text-muted-foreground">{t('WORK.DETAIL.PROBLEM')}</h3>
                    <p className="text-sm leading-relaxed text-destructive">{run.error}</p>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                {conversation && askAi && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            onLeave?.();
                            void askAi.openThread(conversation);
                        }}
                    >
                        {t('WORK.DETAIL.OPEN_CHAT')}
                    </Button>
                )}
                {/* Only studio runs have somewhere of their own to open. */}
                {route && (
                    <Button variant="outline" size="sm" onClick={() => window.location.assign(route)}>
                        {t('WORK.OPEN_STUDIO')}
                    </Button>
                )}
                {paused && (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                            setBusy(true);
                            void studio.resumeAgentRun(run.runId)
                                .then(() => useAgentWorkSignal.getState().runStarted())
                                .finally(() => setBusy(false));
                        }}
                    >
                        {t('WORK.TOAST.CONTINUE')}
                    </Button>
                )}
                {/*
                  * Stopping work that is spending money should be reachable
                  * from the place that shows the spending. Outlined red — it
                  * throws away a job in progress, and the charges already made
                  * stand.
                  */}
                {(live || paused) && (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        onClick={() => {
                            setBusy(true);
                            void studio.cancelAgentRun(run.runId)
                                .then(() => useAgentWorkSignal.getState().runFinished())
                                .finally(() => setBusy(false));
                        }}
                    >
                        {t('WORK.TOAST.STOP')}
                    </Button>
                )}
                <Button variant="ghost" size="sm" onClick={onOpenAll}>
                    {t('WORK.DETAIL.SEE_ALL')}
                </Button>
            </div>
        </div>
    );
};
